import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, catchError, combineLatest, distinctUntilChanged, finalize, forkJoin, map, of, Subscription } from 'rxjs';
import { DashboardComponent, KpiViewModel, AlertConfig, BreakdownPopoverData, DashboardContextPayload, DashboardDrillLevel, DashboardPageVariant, RevenueOverviewMetric, OverviewComparisonRange } from './dashboard.component';
import { FinancialOverviewSelectionRange } from '../../../shared/charts/financial-overview-chart.component';
import { KpiTrend } from '../../../shared/UI/kpi-card/kpi-card.component';
import { evaluateKpiStatus, evaluateKpiTrend } from '../../utils/metric-rules';

const PREVIOUS_PERIOD_KPIS: Record<string, number> = {
  nfi: 12000,
  gross_profit_margin: 92.5,
  ebitda: 7000,
  ebitda_margin: 78.0,
  net_profit_margin: 78.0,
  debtor_days: 120,
  cash_collected_pct: 90,
};
import { ColumnChartDatum } from '../../../shared/charts/revenue-column-chart.component';
import { BreakdownClientRow, BreakdownViewModel, RadialChartDetail, breakdownPalette } from '../../../shared/charts/revenue-radial-chart.component';
import { TableColumn } from '../../../shared/UI/data-table/data-table.component';
import { SummaryRow } from '../../../shared/UI/summary-list/summary-list.component';
import {
  AgedDebtBucketItem,
  ChartData,
  CreditControlClientRow,
  ClientChaseListDrillDownDetailRow,
  DashboardService,
  DashboardKpis,
  DsoTrendItem,
  InvoiceTableRow,
  RevenueByClientItem,
  RevenuePeriodItem,
  FinancialOverviewPoint,
  EvertimePlacementRevenueRow,
  EvertimeGpMarginTrendPoint,
  RevenueConcentrationRow,
  EvertimeGpRow,
  EvertimeClientPerformanceRow,
  ProgressionFinancialMetric,
  CashCollectedApiResponse,
  CashCollectedLedgerDrillDownRow,
  AgedDebtOver90DaysDrillDownRow,
  AgedDebtBucketSummaryViewRow,
  AgedDebtBucketDetailViewRow,
  TurnoverDrillDownSummary,
  TurnoverDrillDownDetailRow,
  CostOfSalesDrillDownDetailRow,
  AdministrativeCostDrillDownSummaryRow,
  AdministrativeCostDrillDownDetailRow,
  FinancialOverviewDailyDrillDetailRow,
  buildCashCollectedLedgerRows
} from '../../../services/apis/dashboard/dashboard.service';
import { ThemeService } from '../../../services/theme.service';
import { CrossFilter, DashboardFilterOption, DashboardStateService, DrillLevel, DrillState, DEFAULT_DASHBOARD_FROM_DATE, DEFAULT_DASHBOARD_TO_DATE } from '../../../services/dashboard-state.service';
import { rangeForPeriodClick, effectiveDateRange, formatDateIso } from '../../utils/date-filter.utils';
import { DsoAsAtService } from '../../../services/apis/dso-as-at/dso-as-at.service';
import { DsoByMonthService } from '../../../services/apis/dso-by-month/dso-by-month.service';
import { CurrentAgedDebtService, CurrentAgedDebtSummary, UnallocatedCashSourceRow } from '../../../services/apis/current-aged-debt/current-aged-debt.service';
import { AgedDebtOver90DaysService } from '../../../services/apis/aged-debt-over-90-days/aged-debt-over-90-days.service';


@Component({
  selector: 'app-dashboard-page',
  standalone: true,
  host: { class: 'flex flex-col flex-1 min-h-0 overflow-hidden' },
  imports: [DashboardComponent],
  templateUrl: './dashboard-page.component.html',
})
export class DashboardPageComponent implements OnInit, OnDestroy {
  private readonly dashboardService = inject(DashboardService);
  private readonly dsoAsAtService = inject(DsoAsAtService);
  private readonly dsoByMonthService = inject(DsoByMonthService);
  private readonly currentAgedDebtService = inject(CurrentAgedDebtService);
  private readonly agedDebtOver90DaysService = inject(AgedDebtOver90DaysService);
  private readonly themeService = inject(ThemeService);
  private readonly dashboardState = inject(DashboardStateService);
  private readonly router = inject(Router);

  private readonly revenueChartKey = 'revenueByMonth';
  private revenueByPeriod: RevenuePeriodItem[] = [];
  private revenueByClient: RevenueByClientItem[] = [];
  private lastChartData: ChartData | null = null;
  private kpiRequestSeq = 0;
  private dsoAsAtRequestSeq = 0;
  private currentAgedDebtRequestSeq = 0;
  private chartRequestSeq = 0;
  private subscriptions = new Subscription();
  revenueDrillState: DrillState = { level: 'month', context: null };
  activeRevenueRangeLabel = 'FY 25/26';
  activeCashFlowRangeLabel = 'FY 25/26';
  breakdownPopoverData: (BreakdownPopoverData & { contactId: string | null }) | null = null;
  activeCrossFilter: CrossFilter | null = null;
  overviewSelectionRange: FinancialOverviewSelectionRange | null = null;
  private revenueManualOverride = true;

  activeTrendLevel: DashboardDrillLevel = 'year';
  private currentInvoiceType = 'all';
  private currentStatus: string[] = [];
  private currentContactId: string | null = null;
  currentStartDate: string | null = null;
  currentEndDate: string | null = null;
  dsoByMonthLoading = false;
  currentAgedDebtLoading = false;
  dataLoading = false;
  comparisonLoading = false;
  comparisonData: DashboardKpis | null = null;
  comparisonPeriodLabel: string | null = null;
  private comparisonDsoValue: number | null = null;
  private comparisonCashCollectedValue: number | null = null;

  get pageDataLoading(): boolean { return this.dataLoading; }
  private effectiveStartDate: string | null = null;
  private effectiveEndDate: string | null = null;
  private readonly cashFlowGranularity$ = new BehaviorSubject<string>('year');
  /** True once the user explicitly picks a Cash Flow granularity; Reset All returns to auto-selection. */
  private cashFlowManualOverride = false;
  private lastResetVersion = 0;
  private readonly refreshSubject = new BehaviorSubject<number>(0);
  /** Cross-highlight index driven by hovering the donut or the client list */
  hoveredBreakdownIndex: number | null = null;
  /** Ordered contactId list matching current breakdownChartData labels (by rank) */
  private breakdownContactIds: (string | null)[] = [];
  /**
   * Persistent contactId → palette-index map built from the full (unfiltered) client list.
   * Survives contact cross-filter changes so drill-down always shows the same colour
   * as the donut slice that was clicked.
   */
  private breakdownColorMap = new Map<string, number>();

  readonly activeMetric$ = new BehaviorSubject<RevenueOverviewMetric>('NFI');
  get activeMetric() { return this.activeMetric$.value; }
  set activeMetric(val: RevenueOverviewMetric) { this.activeMetric$.next(val); }

  activeRevenueTab: string = 'month';

  get chart1Breadcrumb(): string[] {
    return [];
  }

  get pageVariant(): DashboardPageVariant {
    const path = this.router.url.split('?')[0];
    if (path === '/revenue') return 'revenue';
    if (path === '/cash-and-credit-control') return 'cash-credit-control';
    if (path === '/team-performance') return 'team-performance';
    if (path === '/clients') return 'clients';
    return 'overview';
  }

  get pageTitle(): string {
    switch (this.pageVariant) {
      case 'revenue':
        return 'Revenue and Profitability';
      case 'cash-credit-control':
        return 'Cash and Credit Control';
      case 'team-performance':
        return 'Team Performance';
      case 'clients':
        return 'Clients';
      default:
        return 'Financial Dashboard';
    }
  }

  get pageSubtitle(): string {
    switch (this.pageVariant) {
      case 'revenue':
        return 'NFI, margins, and profitability performance';
      case 'cash-credit-control':
        return 'Collections, debtor days, and aged debt';
      case 'team-performance':
        return 'Recruiter revenue, contractor activity, and commission efficiency';
      case 'clients':
        return 'Billed clients and revenue concentration';
      default:
        return 'Recruitment operations - real-time data from Xero';
    }
  }

  get activeChart1MetricForPage(): RevenueOverviewMetric {
    return this.pageVariant === 'revenue' ? 'Revenue' : this.activeMetric;
  }

  get chart1Title(): string {
    if (this.pageVariant === 'revenue') return 'Monthly Revenue Trend';
    return this.chartMetricLabel(this.activeMetric);
  }

  get displayFinancialOverviewData(): FinancialOverviewPoint[] | null {
    return this.financialOverviewData;
  }

  private filterFinancialOverviewByRange(
    rows: FinancialOverviewPoint[] | null,
    startDate: string | null,
    endDate: string | null
  ): FinancialOverviewPoint[] | null {
    if (!rows || !startDate || !endDate) return rows;

    const startMonth = startDate.slice(0, 7);
    const endMonth = endDate.slice(0, 7);
    return rows.filter(row => {
      const month = this.monthKeyFromPeriod(row.period);
      return !!month && month >= startMonth && month <= endMonth;
    });
  }

  get overviewWaterfallData(): FinancialOverviewPoint[] | null {
    if (this.pageVariant !== 'overview' || !this.overviewSelectionRange || !this.financialOverviewData?.length) {
      return this.financialOverviewData;
    }

    const start = this.monthKeyFromPeriod(this.overviewSelectionRange.startPeriod);
    const end = this.monthKeyFromPeriod(this.overviewSelectionRange.endPeriod);
    if (!start || !end) return this.financialOverviewData;
    const [min, max] = start <= end ? [start, end] : [end, start];
    return this.financialOverviewData.filter(row => {
      const key = this.monthKeyFromPeriod(row.period);
      return !!key && key >= min && key <= max;
    });
  }

  onChart1MetricChange(metric: string): void {
    this.activeMetric = metric as RevenueOverviewMetric;
  }

  private chartMetricLabel(metric: RevenueOverviewMetric): string {
    const labels: Record<RevenueOverviewMetric, string> = {
      Revenue: 'Revenue',
      NFI: 'NFI',
      gross_profit_margin: 'Gross Profit Margin',
      ebitda_margin: 'EBITDA Margin %',
      net_profit_margin: 'Net Profit Margin %',
    };

    return labels[metric] ?? 'Revenue';
  }

  onRevenueLevelClick(level: string): void {
    this.revenueManualOverride = true;
    this.activeRevenueTab = level;
  }

  private groupFinancialOverview(rows: FinancialOverviewPoint[], granularity: 'year' | 'quarter'): FinancialOverviewPoint[] {
    const buckets = new Map<string, FinancialOverviewPoint>();

    for (const row of rows) {
      const key = granularity === 'year'
        ? this.financialOverviewYearKey(row)
        : this.financialOverviewQuarterKey(row);
      const existing = buckets.get(key);
      const next: FinancialOverviewPoint = existing ?? {
        period: key,
        periodLabel: granularity === 'year' ? key : this.financialOverviewQuarterLabel(key),
        totalIncome: 0,
        totalExpenses: 0,
        investments: 0,
        savings: 0,
      };

      next.totalIncome += Number(row.totalIncome ?? 0);
      next.totalExpenses += Number(row.totalExpenses ?? 0);
      next.investments = Number(next.investments ?? 0) + Number(row.investments ?? 0);
      next.savings = Number(next.savings ?? 0) + Number(row.savings ?? 0);
      buckets.set(key, next);
    }

    return Array.from(buckets.values()).sort((a, b) => a.period.localeCompare(b.period));
  }

  private financialOverviewYearKey(row: FinancialOverviewPoint): string {
    const period = row.period || row.periodLabel || '';
    const fourDigitYear = period.match(/\d{4}/) ?? row.periodLabel?.match(/\d{4}/);
    if (fourDigitYear) return fourDigitYear[0];

    const shortYear = period.match(/\b(\d{2})\b/) ?? row.periodLabel?.match(/\b(\d{2})\b/);
    return shortYear ? `20${shortYear[1]}` : period;
  }

  private financialOverviewQuarterKey(row: FinancialOverviewPoint): string {
    const period = row.period || row.periodLabel || '';
    const year = this.financialOverviewYearKey(row);
    const existingQuarter = period.match(/Q([1-4])/i) ?? row.periodLabel?.match(/Q([1-4])/i);
    if (existingQuarter) return `${year}-Q${existingQuarter[1]}`;

    const monthMatch = period.match(/^\d{4}-(\d{2})/) ?? row.periodLabel?.match(/^\d{4}-(\d{2})/);
    const month = monthMatch ? Number(monthMatch[1]) : this.financialOverviewMonthNumber(row.periodLabel || period);
    const quarter = Math.max(1, Math.min(4, Math.ceil(month / 3)));
    return `${year}-Q${quarter}`;
  }

  private financialOverviewQuarterLabel(key: string): string {
    const [year, quarter] = key.split('-');
    return `${quarter} ${year}`;
  }

  private financialOverviewMonthNumber(label: string): number {
    const monthNames: Record<string, number> = {
      jan: 1,
      feb: 2,
      mar: 3,
      apr: 4,
      may: 5,
      jun: 6,
      jul: 7,
      aug: 8,
      sep: 9,
      oct: 10,
      nov: 11,
      dec: 12,
    };
    const match = label.toLowerCase().match(/\b(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)\b/);
    return match ? monthNames[match[1]] : 1;
  }

  private lastScrollTime = 0;
  onRevenueChartScroll(event: WheelEvent): void {
    event.stopPropagation();
    const now = Date.now();
    if (now - this.lastScrollTime < 350) return; // 350ms debounce
    this.lastScrollTime = now;

    const levels = ['year', 'quarter', 'month'];
    const currentIndex = levels.indexOf(this.activeRevenueTab);
    
    // Zoom in (scroll up) -> deltaY < 0 -> more detail (index++)
    // Zoom out (scroll down) -> deltaY > 0 -> less detail (index--)
    let nextIndex = event.deltaY < 0 ? currentIndex + 1 : currentIndex - 1;
    
    if (nextIndex < 0) nextIndex = 0;
    if (nextIndex >= levels.length) nextIndex = levels.length - 1;
    
    if (nextIndex !== currentIndex) {
      this.onRevenueLevelClick(levels[nextIndex]);
    }
  }

  get chart2Breadcrumb(): string[] {
    const level = this.activeTrendLevel;
    const items = ['Year'];
    if (level === 'year') {
    } else if (level === 'quarter') {
      items.push('Quarter');
    } else if (level === 'month') {
      items.push('Quarter', 'Month');
    } else if (level === 'week') {
      items.push('Quarter', 'Month', 'Week');
    } else if (level === 'day') {
      items.push('Quarter', 'Month', 'Week', 'Day');
    }
    return items;
  }

  get chart3Breadcrumb(): string[] {
    const selected = this.activeCrossFilter?.contactName;
    const items = ['All Clients'];
    if (selected) {
      items.push(selected);
    }
    return items;
  }

  /** Concentration risk threshold — read from a central config; 25% default */
  private readonly CONCENTRATION_THRESHOLD = 25;

  get breakdownViewModel(): BreakdownViewModel | null {
    if (!this.breakdownChartData || !this.breakdownDetails) return null;
    const labels: string[] = this.breakdownChartData.labels ?? [];
    const values: number[] = this.breakdownChartData.datasets?.[0]?.data ?? [];
    const totalRevenue = values.reduce((s: number, v: number) => s + v, 0);
    const maxBar = values[0] ?? 1; // sorted descending — first is max
    const selectedName = this.activeCrossFilter?.contactName ?? null;
    const palette = breakdownPalette();

    const clients: BreakdownClientRow[] = labels.map((name: string, i: number) => {
      const revenue = values[i] ?? 0;
      const pct = totalRevenue > 0 ? (revenue / totalRevenue) * 100 : 0;
      const contactId = this.breakdownContactIds[i] ?? null;
      const isSelected = selectedName ? name === selectedName : false;

      // Resolve palette index from the persistent color map (keyed by contactId).
      // Falls back to positional index when contactId is unknown (e.g. month-aggregate path).
      const paletteIdx = (contactId && this.breakdownColorMap.has(contactId))
        ? this.breakdownColorMap.get(contactId)!
        : i;
      const color = palette[paletteIdx] ?? palette[palette.length - 1];

      return {
        contactId,
        clientName: name,
        revenue,
        amount: this.breakdownDetails[i]?.amount ?? '£0',
        percentage: pct,
        percentageLabel: this.breakdownDetails[i]?.percentage ?? '0%',
        barWidth: maxBar > 0 ? (revenue / maxBar) * 100 : 0,
        color,
        isSelected,
        isConcentrationRisk: pct >= this.CONCENTRATION_THRESHOLD,
        delta: null,
        deltaLabel: null,
      };
    });

    return {
      clients,
      totalRevenue,
      totalRevenueLabel: this.formatValue(totalRevenue, 'currency'),
      periodLabel: this.activeCrossFilter?.month
        ? this.formatMonthYear(this.activeCrossFilter.month)
        : 'Current period',
      concentrationThreshold: this.CONCENTRATION_THRESHOLD,
      hoveredIndex: this.hoveredBreakdownIndex,
    };
  }

  get clientListRows() {
    if (!this.breakdownChartData || !this.breakdownDetails) return [];
    
    const labels = this.breakdownChartData.labels;
    const data = this.breakdownChartData.datasets[0].data;
    const selected = this.activeCrossFilter?.contactName;
    
    const rows = labels.map((name: string, i: number) => {
      const isSelected = selected ? name === selected : i === 0;
      return {
        name,
        amount: this.breakdownDetails[i]?.amount || '£0',
        percentage: this.breakdownDetails[i]?.percentage || '0%',
        value: data[i] || 0,
        isSelected
      };
    });

    return rows.sort((a: any, b: any) => {
      if (a.isSelected && !b.isSelected) return -1;
      if (!a.isSelected && b.isSelected) return 1;
      return b.value - a.value;
    });
  }

  /* KPI definitions — labels and icons only; values arrive from the API */
  kpis: KpiViewModel[] = [
    { key: 'nfi',                   label: 'Net Fee Income',          icon: 'pi-chart-bar',   value: '', note: 'Awaiting sync', status: 'neutral', trendDirection: null, sourceState: 'loading', ribbonLabel: 'NFI',  periodLabel: 'Current period' },
    { key: 'gross_profit_margin',    label: 'Gross Profit Margin',    icon: 'pi-chart-line',  value: '', note: 'Awaiting sync', status: 'neutral', trendDirection: null, sourceState: 'loading', ribbonLabel: 'GPM',  periodLabel: 'Current period' },
    { key: 'ebitda_margin',          label: 'EBITDA Margin',          icon: 'pi-chart-bar',   value: '', note: 'Awaiting sync', status: 'neutral', trendDirection: null, sourceState: 'loading', ribbonLabel: 'EBITDA', periodLabel: 'Current period' },
    { key: 'net_profit_margin',      label: 'Net Profit Margin',      icon: 'pi-percentage',  value: '', note: 'Awaiting sync', status: 'neutral', trendDirection: null, sourceState: 'loading', ribbonLabel: 'NPM',  periodLabel: 'Current period' },
    { key: 'debtor_days',            label: 'DSO',                      icon: 'pi-calendar',    value: '—', note: null, status: 'neutral', trendDirection: null, sourceState: 'unavailable', ribbonLabel: 'DSO' },
    { key: 'cash_collected_pct',     label: 'Cash Collected / Ledger',  icon: 'pi-wallet',      value: '', note: null, status: 'neutral', trendDirection: null, sourceState: 'unavailable', ribbonLabel: 'CASH', periodLabel: 'Current period' },
    { key: 'aged_debt_90_pct',       label: 'Aged Debt >90 Days',       icon: 'pi-exclamation-triangle', value: '', note: 'Awaiting sync', status: 'neutral', trendDirection: null, sourceState: 'loading', ribbonLabel: 'DEBT', periodLabel: 'Current period' },
    { key: 'operating_cost_pct',     label: 'Total Administrative Cost % Revenue', icon: 'pi-percentage',  value: '', note: 'Awaiting sync', status: 'neutral', trendDirection: null, sourceState: 'loading', ribbonLabel: 'OPEX', periodLabel: 'Current period' },
    { key: 'total_payout',           label: 'Total Payout',             icon: 'pi-wallet',      value: '', note: 'Awaiting sync', status: 'neutral', trendDirection: null, sourceState: 'loading', ribbonLabel: 'PAY', periodLabel: 'Current period' },
    { key: 'commission_payout_ratio', label: 'Commission Payout Ratio', icon: 'pi-wallet', value: '', note: 'Awaiting sync', status: 'neutral', trendDirection: null, sourceState: 'loading', ribbonLabel: 'COMM', periodLabel: 'Current period' },
    { key: 'revenue_concentration',  label: 'Top 5 Clients % of Revenue', icon: 'pi-sitemap', value: '', note: 'Awaiting sync', status: 'neutral', trendDirection: null, sourceState: 'loading', ribbonLabel: 'TOP5', periodLabel: 'Current period' },
    { key: 'top_client_revenue_pct', label: 'Top Client % of Revenue', icon: 'pi-chart-pie', value: '', note: 'Awaiting sync', status: 'neutral', trendDirection: null, sourceState: 'loading', ribbonLabel: 'TOP', periodLabel: 'Current period' },
    { key: 'top_5_clients_revenue_pct', label: 'Top 5 Clients % of Revenue', icon: 'pi-sitemap', value: '', note: 'Awaiting sync', status: 'neutral', trendDirection: null, sourceState: 'loading', ribbonLabel: 'TOP5', periodLabel: 'Current period' },
    { key: 'clients_billed', label: 'Number of Clients Billed', icon: 'pi-briefcase', value: '', note: 'Awaiting sync', status: 'neutral', trendDirection: null, sourceState: 'loading', ribbonLabel: 'CLNT', periodLabel: 'Current period' },
  ];

  /* Invoice table columns */
  readonly invoiceColumns: TableColumn[] = [
    { header: 'Invoice #', field: 'invoiceNumber' },
    { header: 'Client',    field: 'clientName'    },
    { header: 'Date',      field: 'invoiceDate'   },
    { header: 'Due',       field: 'dueDate'       },
    { header: 'Status',    field: 'status'        },
    { header: 'Total',     field: 'total'         },
  ];

  /* Data rows for tables */
  tableRows: any[] | null = null;

  /* Chart data and options */
  financialOverviewData: FinancialOverviewPoint[] | null = null;
  marginComparisonData: FinancialOverviewPoint[] = [];
  operatingCostPercentData: FinancialOverviewPoint[] = [];
  financialOverviewDrillDetail: FinancialOverviewPoint[] = [];
  progressionOfFinancialMetrics: ProgressionFinancialMetric[] = [];
  cashCollectedLedgerDrillDown: CashCollectedLedgerDrillDownRow[] = [];
  cashCollectedApiResponse: CashCollectedApiResponse | null = null;
  agedDebtOver90DaysDrillDown: AgedDebtOver90DaysDrillDownRow[] = [];
  agedDebtBucketSummaryView: AgedDebtBucketSummaryViewRow[] = [];
  agedDebtBucketDetailView: AgedDebtBucketDetailViewRow[] = [];
  turnoverDrillDownSummary: TurnoverDrillDownSummary | null = null;
  turnoverDrillDownDetail: TurnoverDrillDownDetailRow[] = [];
  costOfSalesDrillDownDetail: CostOfSalesDrillDownDetailRow[] = [];
  administrativeCostDrillDownSummary: AdministrativeCostDrillDownSummaryRow[] = [];
  administrativeCostDrillDownDetail: AdministrativeCostDrillDownDetailRow[] = [];
  financialOverviewDailyDrillDetail: FinancialOverviewDailyDrillDetailRow[] = [];
  placementRevenuePerMonth: EvertimePlacementRevenueRow[] = [];
  gpmPermVsTempByMonth: EvertimeGpMarginTrendPoint[] = [];
  trendChartData: any = null;
  trendChartOptions: any = null;
  agedDebtBuckets: AgedDebtBucketItem[] = [];
  currentAgedDebtSummary: CurrentAgedDebtSummary | null = null;
  unallocatedCashRows: UnallocatedCashSourceRow[] = [];
  agedDebtAsAtLabel = '';
  dsoTrend: DsoTrendItem[] = [];
  dsoTargetDays = 45;
  creditControlClients: CreditControlClientRow[] = [];
  clientChaseListDrillDownDetail: ClientChaseListDrillDownDetailRow[] = [];
  revenueConcentration: RevenueConcentrationRow[] = [];
  top10ClientsByGrossProfit: EvertimeGpRow[] = [];
  clientPerformance: EvertimeClientPerformanceRow[] = [];
  breakdownChartData: any = null;
  breakdownDetails: RadialChartDetail[] = [];
  breakdownCenterValue = '';
  breakdownCenterLabel = '';

  /* Activity & Alerts — populated from API once sync runs */
  alerts: AlertConfig[] = [];

  /* Summary list — populated from API once sync runs */
  summaryRows: SummaryRow[] = [];

  ngOnInit(): void {
    // 1. Re-render chart options/data whenever the active theme changes.
    //    (The Revenue by Month column chart self-handles theme switches internally
    //    via amCharts; only the remaining Chart.js charts need their options rebuilt.)
    this.subscriptions.add(
      this.themeService.isDark$.subscribe(() => {
        const options = this.getChartOptions();
        this.trendChartOptions = options.trend;
        if (this.lastChartData) {
          this.setupChartData(this.lastChartData);
        }
      })
    );

    // 2. Drive dashboard data fetches off drill state, slicers, date range, chart cross-filter, and cash flow granularity.
    this.subscriptions.add(
      combineLatest([
        this.dashboardState.drillState$.pipe(
          map(stateMap => stateMap.get(this.revenueChartKey) ?? { level: 'month' as DrillLevel, context: null }),
          distinctUntilChanged((a, b) => a.level === b.level && a.context === b.context),
        ),
        this.dashboardState.activeSlicers$,
        this.dashboardState.globalDateRange$,
        this.dashboardState.crossFilter$,
        this.cashFlowGranularity$,
        this.dashboardState.resetVersion$,
        this.refreshSubject
      ]).subscribe(([drillState, slicers, dateRange, crossFilter, granularity, resetVersion, _refresh]) => {
        if (resetVersion !== this.lastResetVersion) {
          this.lastResetVersion = resetVersion;
          this.revenueManualOverride = false;
          this.activeRevenueTab = 'month';
          this.overviewSelectionRange = null;
          this.resetCashFlowGranularity();
          if (granularity !== 'year') {
            return;
          }
        }

        const hasPeriodCrossFilter = !!(
          crossFilter?.year
          || crossFilter?.quarter
          || crossFilter?.month
          || crossFilter?.week
          || crossFilter?.day
          || crossFilter?.startDate
          || crossFilter?.endDate
        );
        if (this.overviewSelectionRange && !hasPeriodCrossFilter) {
          this.overviewSelectionRange = null;
        }

        this.activeCrossFilter = crossFilter;
        const startStr = dateRange?.startDate ? this.formatDateIso(dateRange.startDate) : null;
        const endStr = dateRange?.endDate ? this.formatDateIso(dateRange.endDate) : null;
        const effectiveRange = this.effectiveDateRange(startStr, endStr, crossFilter);
        const effectiveRevenueDrill = this.effectiveRevenueDrillState(drillState, effectiveRange.startDate, effectiveRange.endDate);
        this.revenueDrillState = effectiveRevenueDrill;
        this.activeRevenueRangeLabel = this.revenueRangeLabel(effectiveRange.startDate, effectiveRange.endDate);
        this.activeCashFlowRangeLabel = this.activeRevenueRangeLabel;
        const effectiveContactId = slicers.contactId ?? crossFilter?.contactId ?? null;
        const effectiveGranularity = this.effectiveCashFlowGranularity(granularity, effectiveRange.startDate, effectiveRange.endDate);
        // Keep activeTrendLevel in sync for template bindings
        this.activeTrendLevel = effectiveGranularity as DashboardDrillLevel;
        this.currentInvoiceType = slicers.invoiceType ?? 'all';
        this.currentStatus = slicers.status ?? [];
        this.currentContactId = effectiveContactId;
        this.currentStartDate = effectiveRange.startDate;
        this.currentEndDate = effectiveRange.endDate;
        this.effectiveStartDate = effectiveRange.startDate;
        this.effectiveEndDate = effectiveRange.endDate;

        // KPI cards now come from the replacement financial dashboard endpoint.
        // Chart data remains disabled until its replacement mapping is added.
        this.loadKpis(
          effectiveRange.startDate,
          effectiveRange.endDate,
          this.currentStatus,
          this.currentContactId,
          this.currentInvoiceType
        );
      })
    );

    this.subscriptions.add(
      this.dashboardState.comparisonDateRange$
        .pipe(distinctUntilChanged((left, right) =>
          left?.startDate.getTime() === right?.startDate.getTime() &&
          left?.endDate.getTime() === right?.endDate.getTime()
        ))
        .subscribe(range => this.onOverviewComparisonApply(range ? {
          startDate: this.formatDateIso(range.startDate),
          endDate: this.formatDateIso(range.endDate),
        } : null))
    );

    // 3. Refresh dashboard data when topbar Sync Now button completes.
    this.subscriptions.add(
      this.dashboardState.syncCompleted$.subscribe(() => {
        this.onSyncCompleted();
      })
    );

  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  get chart1ShowDrillUp(): boolean {
    return this.revenueDrillState.level !== 'year';
  }

  get chart1Filtered(): boolean {
    return !!this.activeCrossFilter?.contactId || !!this.overviewSelectionRange;
  }

  get chart2Filtered(): boolean {
    return !!(this.activeCrossFilter?.contactName || this.activeCrossFilter?.year ||
              this.activeCrossFilter?.quarter || this.activeCrossFilter?.month ||
              this.activeCrossFilter?.week || this.activeCrossFilter?.day ||
              this.activeCrossFilter?.startDate);
  }

  get chart3Filtered(): boolean {
    return !!(this.activeCrossFilter?.year || this.activeCrossFilter?.quarter ||
              this.activeCrossFilter?.month || this.activeCrossFilter?.week ||
              this.activeCrossFilter?.day || this.activeCrossFilter?.contactName ||
              this.activeCrossFilter?.startDate);
  }

  get crossFilterActive(): boolean {
    return !!this.activeCrossFilter;
  }

  onSyncCompleted(): void {
    this.refreshSubject.next(this.refreshSubject.value + 1);
    this.loadDsoAsAt(this.currentEndDate);
  }

  private loadDsoAsAt(endDate: string | null | undefined): void {
    const asOfDate = endDate ?? this.formatDateIso(new Date());
    const asOfDateLabel = this.formatDsoAsAtLabel(asOfDate);
    const requestSeq = ++this.dsoAsAtRequestSeq;
    this.kpis = this.kpis.map(kpi => kpi.key === 'debtor_days'
      ? { ...kpi, sourceState: 'loading', periodLabel: asOfDateLabel }
      : kpi);

    this.dsoAsAtService.getDsoAsAt(asOfDate).subscribe({
      next: result => {
        if (requestSeq !== this.dsoAsAtRequestSeq) return;
        this.kpis = this.kpis.map(kpi => {
          if (kpi.key !== 'debtor_days') return kpi;
          const hasValue = result.value != null;
          return {
            ...kpi,
            value: hasValue
              ? new Intl.NumberFormat('en-GB', { maximumFractionDigits: 2 }).format(result.value!)
              : '—',
            note: null,
            status: hasValue ? evaluateKpiStatus(kpi.key, result.value!) : 'neutral',
            trendDirection: null,
            trend: undefined,
            sourceState: hasValue ? 'current' : 'unavailable',
            periodLabel: asOfDateLabel,
            previousValue: undefined,
          };
        });
      },
      error: error => {
        if (requestSeq !== this.dsoAsAtRequestSeq) return;
        this.kpis = this.kpis.map(kpi => kpi.key === 'debtor_days'
          ? {
              ...kpi,
              value: '—',
              note: null,
              status: 'neutral',
              trendDirection: null,
              trend: undefined,
              sourceState: 'unavailable',
              periodLabel: asOfDateLabel,
              previousValue: undefined,
            }
          : kpi);
        console.error('Failed to load DSO As At data:', error);
      },
    });
  }

  private formatDsoAsAtLabel(isoDate: string): string {
    const [year, month, day] = isoDate.split('-').map(Number);
    if (!year || !month || !day) return isoDate;
    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      timeZone: 'UTC',
    }).format(new Date(Date.UTC(year, month - 1, day)));
  }

  private loadCurrentAgedDebt(endDate: string | null | undefined): void {
    const asOfDate = endDate ?? this.formatDateIso(new Date());
    this.agedDebtAsAtLabel = this.formatDsoAsAtLabel(asOfDate);
    const requestSeq = ++this.currentAgedDebtRequestSeq;
    this.currentAgedDebtLoading = true;
    this.currentAgedDebtService.getCurrentAgedDebt(asOfDate).pipe(
      finalize(() => {
        if (requestSeq === this.currentAgedDebtRequestSeq) this.currentAgedDebtLoading = false;
      })
    ).subscribe({
      next: result => {
        if (requestSeq !== this.currentAgedDebtRequestSeq) return;
        this.agedDebtBuckets = result.buckets;
        this.currentAgedDebtSummary = result.summary;
        this.creditControlClients = result.clients;
        this.unallocatedCashRows = result.unallocatedCashRows;
      },
      error: error => {
        if (requestSeq !== this.currentAgedDebtRequestSeq) return;
        this.unallocatedCashRows = [];
        this.agedDebtBuckets = [];
        this.currentAgedDebtSummary = null;
        this.creditControlClients = [];
        console.error('Failed to load Current Aged Debt data:', error);
      },
    });
  }

    // 2. Period dimension — most specific wins for display
  private formatMonthYear(monthStr: string): string {
    const [year, month] = monthStr.split('-').map(Number);
    if (!year || !month) return monthStr;
    const date = new Date(year, month - 1, 1);
    return date.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
  }

  private revenueContextMonthLabel(context: string): string {
    return this.formatMonthYear(context.length >= 7 ? context.substring(0, 7) : context);
  }

  private quarterKeyForMonth(monthKey: string): string {
    const [year, month] = monthKey.split('-').map(Number);
    const quarter = Math.floor((month - 1) / 3) + 1;
    return `${year}-Q${quarter}`;
  }

  private quarterKeyForDate(dateKey: string): string {
    return this.quarterKeyForMonth(dateKey.substring(0, 7));
  }

  private formatDateRange(start: Date, end: Date): string {
    const fmt = (d: Date) => d.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
    if (fmt(start) === fmt(end)) {
      return fmt(start);
    }
    const fmtShort = (d: Date) => d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
    return `${fmtShort(start)} - ${fmtShort(end)}`;
  }

  private formatExactDateRange(start: Date, end: Date): string {
    const fmt = (d: Date) => d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
    return `${fmt(start)} - ${fmt(end)}`;
  }

  onChart1BreadcrumbClick(index: number): void {
    if (index === 0) {
      this.revenueManualOverride = true;
      this.dashboardState.resetDrillState(this.revenueChartKey);
      this.overviewSelectionRange = null;
      this.dashboardState.clearPeriodFilter();
    }
  }

  onBreakdownSegmentClick(index: number): void {
    // Use full revenueByClient from the latest response for index resolution
    const source = this.revenueByClient;
    const item = source[index];
    if (!item) return;
    const total = source.reduce((sum, x) => sum + x.revenue, 0);
    const percentage = total > 0 ? (item.revenue / total) * 100 : 0;
    this.breakdownPopoverData = {
      contactId: item.contactId,
      contactName: item.clientName,
      amount: this.formatValue(item.revenue, 'currency'),
      percentage: `${percentage.toFixed(1)}%`,
    };
    if ((item.contactId && this.activeCrossFilter?.contactId === item.contactId) ||
        (!item.contactId && this.activeCrossFilter?.contactName === item.clientName)) {
      this.dashboardState.clearContactFilter();
      return;
    }
    // Merge contact into existing period filter (intersection)
    this.dashboardState.mergeCrossFilter({
      contactId: item.contactId ?? undefined,
      contactName: item.clientName
    });
  }

  onViewInvoicesClick(): void {
    if (!this.breakdownPopoverData?.contactId) return;
    this.navigateToRecords(null, this.breakdownPopoverData.contactId);
  }

  onRevenueBarClick(index: number): void {
    const rows = this.displayFinancialOverviewData;
    const item = rows?.[index];
    if (!item?.period) return;

    if (this.pageVariant === 'overview') {
      this.applyOverviewMonthClick(item.period);
      return;
    }

    this.applyChartPeriodClick(item.period, this.mapRevenueTabToDrillLevel(this.activeRevenueTab));
  }

  onEbitdaBarClick(index: number): void {
    const rows = this.displayFinancialOverviewData;
    const item = rows?.[index];
    if (!item?.period) return;
    this.applyChartPeriodClick(item.period, 'month');
  }

  private mapRevenueTabToDrillLevel(tab: string): DrillLevel {
    if (tab === 'year') return 'year';
    if (tab === 'quarter') return 'quarter';
    return 'month';
  }

  private applyChartPeriodClick(period: string, level: DrillLevel): void {
    this.revenueManualOverride = true;

    const range = this.rangeForPeriodClick(period, level);
    if (this.isSamePeriodFilter(range)) {
      this.dashboardState.clearPeriodFilter();
      return;
    }

    this.applyPeriodCrossFilter(range);
    const nextLevel = this.nextRevenueLevel(level);
    if (nextLevel) {
      this.dashboardState.setDrillState(this.revenueChartKey, nextLevel, period);
    }
  }

  private rangeForPeriodClick(period: string, level: DrillLevel): Partial<CrossFilter> {
    return rangeForPeriodClick(period, level);
  }

  private applyOverviewMonthClick(period: string): void {
    const monthKey = this.monthKeyFromPeriod(period);
    if (!monthKey) return;

    if (
      this.overviewSelectionRange?.startPeriod === monthKey &&
      this.overviewSelectionRange.endPeriod === monthKey
    ) {
      this.onOverviewSelectionClear();
      return;
    }

    this.revenueManualOverride = true;
    this.activeRevenueTab = 'month';
    this.dashboardState.setDrillState(this.revenueChartKey, 'month', null);
    this.applyOverviewSelectionRange({ startPeriod: monthKey, endPeriod: monthKey });
  }

  private monthKeyFromPeriod(period: string): string | null {
    const match = period.match(/^(\d{4}-\d{2})/);
    return match ? match[1] : null;
  }

  onOverviewSelectionRangeChange(range: FinancialOverviewSelectionRange): void {
    this.applyOverviewSelectionRange(range);
  }

  private applyOverviewSelectionRange(range: FinancialOverviewSelectionRange): void {
    const normalized = this.normalizeOverviewSelectionRange(range);
    this.overviewSelectionRange = normalized;

    const dateRange = this.dateRangeForOverviewSelection(normalized);
    const singleMonth = normalized.startPeriod === normalized.endPeriod
      ? this.monthKeyFromPeriod(normalized.startPeriod)
      : null;
    this.applyPeriodCrossFilter({
      month: singleMonth ?? undefined,
      startDate: dateRange.startDate ?? undefined,
      endDate: dateRange.endDate ?? undefined,
    });
  }

  onOverviewSelectionClear(): void {
    this.overviewSelectionRange = null;
    this.dashboardState.clearPeriodFilter();
  }

  onOverviewMonthShowTable(index: number): void {
    const item = this.displayFinancialOverviewData?.[index];
    if (!item?.period) return;
    const monthKey = this.monthKeyFromPeriod(item.period);
    if (!monthKey) return;
    const range = this.rangeForPeriodClick(monthKey, 'month');
    this.navigateToRecordsForRange(range.startDate ?? null, range.endDate ?? null);
  }

  private normalizeOverviewSelectionRange(range: FinancialOverviewSelectionRange): FinancialOverviewSelectionRange {
    const start = this.monthKeyFromPeriod(range.startPeriod) ?? range.startPeriod;
    const end = this.monthKeyFromPeriod(range.endPeriod) ?? range.endPeriod;
    return start <= end
      ? { startPeriod: start, endPeriod: end }
      : { startPeriod: end, endPeriod: start };
  }

  private dateRangeForOverviewSelection(range: FinancialOverviewSelectionRange): { startDate: string | null; endDate: string | null } {
    const startRange = this.rangeForPeriodClick(range.startPeriod, 'month');
    const endRange = this.rangeForPeriodClick(range.endPeriod, 'month');
    return {
      startDate: startRange.startDate ?? null,
      endDate: endRange.endDate ?? null,
    };
  }

  private applyPeriodCrossFilter(range: Partial<CrossFilter>): void {
    const update = {
      year: null,
      quarter: null,
      month: null,
      week: null,
      day: null,
      startDate: null,
      endDate: null,
      ...range,
    } as any;
    this.dashboardState.mergeCrossFilter(update);
  }

  private isSamePeriodFilter(range: Partial<CrossFilter>): boolean {
    const current = this.activeCrossFilter;
    if (!current) return false;
    return (!!range.year && current.year === range.year) ||
      (!!range.quarter && current.quarter === range.quarter) ||
      (!!range.month && current.month === range.month) ||
      (!!range.week && current.week === range.week) ||
      (!!range.day && current.day === range.day);
  }

  private nextRevenueLevel(level: DrillLevel): DrillLevel | null {
    const order: DrillLevel[] = ['year', 'quarter', 'month', 'week', 'day'];
    return order[order.indexOf(level) + 1] ?? null;
  }

  private previousRevenueLevel(level: DrillLevel): DrillLevel | null {
    const order: DrillLevel[] = ['year', 'quarter', 'month', 'week', 'day'];
    return order[order.indexOf(level) - 1] ?? null;
  }
  private formatMonthLabel(monthKey: string): string {
    const [year, month] = monthKey.split('-').map(Number);
    return new Date(year, month - 1, 1).toLocaleDateString('en-GB', { month: 'short', year: '2-digit' });
  }

  private formatWeekLabel(weekStartIso: string): string {
    const start = new Date(weekStartIso);
    const end = new Date(start);
    end.setDate(end.getDate() + 6);
    const fmt = (d: Date) => d.toLocaleDateString('en-GB', { month: 'short', day: 'numeric' });
    return `${fmt(start)} - ${fmt(end)}`;
  }

  private formatDateIso(date: Date): string {
    return formatDateIso(date);
  }

  private buildKpiTrend(
    current: number,
    previous: number,
    format: string,
    direction: 'up' | 'down' | 'flat' | null
  ): KpiTrend | undefined {
    if (direction === null) return undefined;

    if (format === 'days') {
      const delta = Math.round(current - previous);
      const sign = delta > 0 ? '+' : '';
      return {
        direction: delta <= 0 ? 'up' : 'down',
        value: `${sign}${delta}d`,
        label: 'vs prior period',
      };
    }

    if (format === 'percent') {
      const delta = current - previous;
      const sign = delta >= 0 ? '+' : '';
      return {
        direction: delta >= 0 ? 'up' : 'down',
        value: `${sign}${delta.toFixed(1)}%`,
        label: 'vs prior period',
      };
    }

    if (previous === 0) return undefined;
    const pct = ((current - previous) / Math.abs(previous)) * 100;
    const sign = pct >= 0 ? '+' : '';
    return {
      direction: direction === 'flat' ? 'up' : direction,
      value: `${sign}${pct.toFixed(1)}%`,
      label: 'vs prior period',
    };
  }

  private loadKpis(
    startDate?: string | null,
    endDate?: string | null,
    status?: string[],
    contactId?: string | null,
    invoiceType?: string
  ): void {
    this.loadDsoAsAt(endDate);
    this.loadCurrentAgedDebt(endDate);
    const requestSeq = ++this.kpiRequestSeq;
    this.dataLoading = true;
    this.agedDebtOver90DaysDrillDown = [];
    this.kpis = this.kpis.map(kpi => kpi.key === 'aged_debt_90_pct'
      ? { ...kpi, sourceState: 'loading' }
      : kpi);
    let pendingRequests = 4;
    const completeRequest = (): void => {
      pendingRequests -= 1;
      if (requestSeq === this.kpiRequestSeq && pendingRequests === 0) {
        this.dataLoading = false;
      }
    };

    this.dashboardService
      .getCashCollected(startDate, endDate)
      .pipe(finalize(completeRequest))
      .subscribe({
      next: response => {
        if (requestSeq !== this.kpiRequestSeq) return;
        this.cashCollectedApiResponse = response;
        this.cashCollectedLedgerDrillDown = buildCashCollectedLedgerRows(response.data);

        const latestMonth = this.cashCollectedLedgerDrillDown.at(-1);
        const cashCollectedPercent = latestMonth?.cumulativeCashCollectedPercent ?? null;
        this.kpis = this.kpis.map(kpi => kpi.key === 'cash_collected_pct'
          ? {
              ...kpi,
              value: cashCollectedPercent == null
                ? '—'
                : this.formatValue(cashCollectedPercent, 'percent'),
              note: null,
              status: cashCollectedPercent == null
                ? 'neutral'
                : evaluateKpiStatus(kpi.key, cashCollectedPercent),
              trendDirection: null,
              trend: undefined,
              sourceState: cashCollectedPercent == null ? 'unavailable' : 'current',
              periodLabel: response.data?.kpi
                ? `${response.data.kpi.startDate} to ${response.data.kpi.endDate}`
                : kpi.periodLabel,
              previousValue: undefined,
            }
          : kpi);
      },
      error: error => {
        if (requestSeq !== this.kpiRequestSeq) return;
        this.cashCollectedApiResponse = null;
        this.cashCollectedLedgerDrillDown = [];
        this.kpis = this.kpis.map(kpi => kpi.key === 'cash_collected_pct'
          ? {
              ...kpi,
              value: '—',
              note: null,
              status: 'neutral',
              trendDirection: null,
              trend: undefined,
              sourceState: 'unavailable',
              previousValue: undefined,
            }
          : kpi);
        console.error('Failed to load Cash Collected data:', error);
      },
    });

    this.dsoByMonthLoading = true;
    this.dsoByMonthService
      .getDsoByMonth(startDate ?? null, endDate ?? null)
      .pipe(finalize(() => {
        completeRequest();
        if (requestSeq === this.kpiRequestSeq) this.dsoByMonthLoading = false;
      }))
      .subscribe({
        next: rows => {
          if (requestSeq !== this.kpiRequestSeq) return;
          this.dsoTrend = rows;
        },
        error: error => {
          if (requestSeq !== this.kpiRequestSeq) return;
          this.dsoTrend = [];
          console.error('Failed to load DSO by Month data:', error);
        },
      });

    this.agedDebtOver90DaysService
      .getAgedDebtOver90Days(endDate ?? null)
      .pipe(finalize(completeRequest))
      .subscribe({
        next: result => {
          if (requestSeq !== this.kpiRequestSeq) return;
          this.agedDebtOver90DaysDrillDown = result.rows;
          this.kpis = this.kpis.map(kpi => kpi.key === 'aged_debt_90_pct'
            ? {
                ...kpi,
                value: result.value == null ? '—' : this.formatValue(result.value, 'percent', 2),
                note: null,
                status: result.value == null
                  ? 'neutral'
                  : evaluateKpiStatus(kpi.key, result.value),
                trendDirection: null,
                trend: undefined,
                sourceState: result.value == null ? 'unavailable' : 'current',
                periodLabel: this.activeFilterLabel(startDate, endDate, contactId),
                previousValue: undefined,
              }
            : kpi);
        },
        error: error => {
          if (requestSeq !== this.kpiRequestSeq) return;
          this.agedDebtOver90DaysDrillDown = [];
          this.kpis = this.kpis.map(kpi => kpi.key === 'aged_debt_90_pct'
            ? {
                ...kpi,
                value: '—',
                note: null,
                status: 'neutral',
                trendDirection: null,
                trend: undefined,
                sourceState: 'unavailable',
                previousValue: undefined,
              }
            : kpi);
          console.error('Failed to load Aged Debt >90 Days data:', error);
        },
      });

    this.dashboardService
      .getKpis(startDate, endDate, status, contactId, invoiceType)
      .pipe(finalize(completeRequest))
      .subscribe({
      next: (res) => {
        if (requestSeq !== this.kpiRequestSeq) return;
        if (res && res.kpis) {
          this.placementRevenuePerMonth = res.placementRevenuePerMonth ?? [];
          this.gpmPermVsTempByMonth = res.gpmPermVsTempByMonth ?? [];
          // Client Chase List is loaded by the Current Aged Debt endpoint.
          this.clientChaseListDrillDownDetail = [];
          this.revenueConcentration = res.revenueConcentration ?? [];
          this.top10ClientsByGrossProfit = res.top10ClientsByGrossProfit ?? [];
          this.clientPerformance = res.clientPerformance ?? [];
          this.marginComparisonData = res.marginComparison ?? [];
          this.operatingCostPercentData = res.operatingCostPercentOfRevenue ?? [];
          this.progressionOfFinancialMetrics = res.progressionOfFinancialMetrics ?? [];
          // Aged Debt >90 Days is loaded independently from its dedicated endpoint.
          this.agedDebtBucketSummaryView = [];
          this.agedDebtBucketDetailView = [];
          this.turnoverDrillDownSummary = res.turnoverDrillDownSummary ?? null;
          this.turnoverDrillDownDetail = res.turnoverDrillDownDetail ?? [];
          this.costOfSalesDrillDownDetail = res.costOfSalesDrillDownDetail ?? [];
          this.administrativeCostDrillDownSummary = res.administrativeCostDrillDownSummary ?? [];
          this.administrativeCostDrillDownDetail = res.administrativeCostDrillDownDetail ?? [];
          this.financialOverviewDailyDrillDetail = res.financialOverviewDailyDrillDetail ?? [];
          if (!this.overviewSelectionRange) {
            this.financialOverviewData = this.filterFinancialOverviewByRange(
              res.financialOverview ?? [],
              startDate ?? null,
              endDate ?? null
            ) ?? [];
            this.financialOverviewDrillDetail = res.financialOverviewDrillDetail ?? [];
          }
          this.kpis = this.kpis.map(k => {
            if (k.key === 'aged_debt_90_pct') {
              return k;
            }
            if (k.key === 'debtor_days' || k.key === 'cash_collected_pct') {
              return k;
            }
            const found = res.kpis.find(x => x.key === k.key);
            if (found) {
              const hasValue = found.value !== null && found.value !== undefined;
              const sourceState = found.sourceState ?? (hasValue ? 'current' : 'unavailable');
              const isCurrent = sourceState === 'current' && hasValue;
              
              // Evaluate status dynamically from the central rules module using current calculated value
              const statusVal = isCurrent ? evaluateKpiStatus(k.key, found.value!) : 'neutral';
              
              // Evaluate trend dynamically by comparing current with actual previous period value
              const prevVal = PREVIOUS_PERIOD_KPIS[k.key] !== undefined ? PREVIOUS_PERIOD_KPIS[k.key] : null;
              const trendVal = isCurrent ? evaluateKpiTrend(k.key, found.value!, prevVal) : null;
              const trendMeta = isCurrent && prevVal !== null && found.value !== null
                ? this.buildKpiTrend(found.value, prevVal, found.format, trendVal)
                : undefined;

              // Backend decides when a card needs a source/scope note (e.g. approximated
              // from invoices, or whole-company and unaffected by client/status/type filters).
              const noteVal = found.note || null;

              return {
                ...k,
                value: hasValue ? this.formatValue(found.value, found.format) : '—',
                note: noteVal,
                status: statusVal,
                trendDirection: trendVal,
                trend: trendMeta,
                sourceState,
                periodLabel: res.periodLabel ?? k.periodLabel ?? 'Current period',
                previousValue: prevVal !== null ? this.formatValue(prevVal, found.format) : undefined
              };
            }
            return {
              ...k,
              value: '—',
              note: null,
              status: 'neutral',
              trendDirection: null,
              sourceState: 'unavailable'
            };
          });
          this.applyComparisonKpis();

          this.summaryRows = [
            { label: 'Reporting Period', value: res.periodLabel ?? this.activeFilterLabel(startDate, endDate, contactId) },
            { label: 'Active Connection', value: 'Xero Integration' },
            { label: 'Sync Status', value: 'Up to date' },
            { label: 'Calculated At', value: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) }
          ];
        }
      },
      error: (err) => {
        if (requestSeq !== this.kpiRequestSeq) return;
        console.error('Failed to load KPIs:', err);
        this.kpis = this.kpis.map(k => k.key === 'debtor_days' || k.key === 'cash_collected_pct' ? k : ({
          ...k,
          value: '—',
          note: null,
          status: 'neutral',
          trendDirection: null,
          sourceState: 'unavailable'
        }));
      }
    });
  }

  onOverviewComparisonApply(range: OverviewComparisonRange | null): void {
    if (!range) {
      this.comparisonLoading = false;
      this.comparisonData = null;
      this.comparisonPeriodLabel = null;
      this.comparisonDsoValue = null;
      this.comparisonCashCollectedValue = null;
      this.applyComparisonKpis();
      return;
    }

    this.comparisonLoading = true;
    forkJoin({
      dashboard: this.dashboardService.getKpis(
        range.startDate,
        range.endDate,
        this.currentStatus,
        this.currentContactId,
        this.currentInvoiceType
      ),
      dso: this.dsoAsAtService.getDsoAsAt(range.endDate).pipe(
        catchError(error => {
          console.error('Failed to load comparison DSO As At data:', error);
          return of(null);
        })
      ),
      cashCollected: this.dashboardService.getCashCollected(range.startDate, range.endDate).pipe(
        catchError(error => {
          console.error('Failed to load comparison Cash Collected data:', error);
          return of(null);
        })
      ),
    }).subscribe({
      next: ({ dashboard: response, dso, cashCollected }) => {
        this.comparisonLoading = false;
        const comparisonCashCollectedRows: CashCollectedLedgerDrillDownRow[] =
          buildCashCollectedLedgerRows(cashCollected?.data);
        this.comparisonData = {
          ...response,
          cashCollectedLedgerDrillDown: comparisonCashCollectedRows,
          financialOverview: this.filterFinancialOverviewByRange(
            response.financialOverview ?? [],
            range.startDate,
            range.endDate
          ) ?? [],
        };
        this.comparisonDsoValue = dso?.value ?? null;
        const latestComparisonCashMonth = comparisonCashCollectedRows.at(-1);
        this.comparisonCashCollectedValue = latestComparisonCashMonth?.cumulativeCashCollectedPercent == null
          ? null
          : Number(latestComparisonCashMonth.cumulativeCashCollectedPercent);
        this.comparisonPeriodLabel = response.periodLabel ?? `${range.startDate} to ${range.endDate}`;
        this.applyComparisonKpis();
      },
      error: error => {
        this.comparisonLoading = false;
        this.comparisonData = null;
        this.comparisonPeriodLabel = null;
        this.comparisonDsoValue = null;
        this.comparisonCashCollectedValue = null;
        this.applyComparisonKpis();
        console.error('Failed to load comparison KPIs:', error);
      }
    });
  }

  private applyComparisonKpis(): void {
    this.kpis = this.kpis.map(kpi => {
      if (kpi.key === 'debtor_days') {
        return {
          ...kpi,
          comparisonValue: this.comparisonData && this.comparisonDsoValue != null
            ? new Intl.NumberFormat('en-GB', { maximumFractionDigits: 2 }).format(this.comparisonDsoValue)
            : undefined,
          comparisonPeriodLabel: this.comparisonData ? this.comparisonPeriodLabel ?? undefined : undefined,
        };
      }
      if (kpi.key === 'cash_collected_pct') {
        return {
          ...kpi,
          comparisonValue: this.comparisonData && this.comparisonCashCollectedValue != null
            ? this.formatValue(this.comparisonCashCollectedValue, 'percent')
            : undefined,
          comparisonPeriodLabel: this.comparisonData ? this.comparisonPeriodLabel ?? undefined : undefined,
        };
      }
      const comparison = this.comparisonData?.kpis.find(item => item.key === kpi.key);
      const comparisonFormat = comparison?.format ?? this.kpiValueFormat(kpi.key);
      return {
        ...kpi,
        comparisonValue: this.comparisonData
          ? this.formatValue(comparison?.value ?? 0, comparisonFormat)
          : undefined,
        comparisonPeriodLabel: this.comparisonData ? this.comparisonPeriodLabel ?? undefined : undefined,
      };
    });
  }

  private kpiValueFormat(key: string): string {
    if (key === 'nfi') return 'currency';
    if (key === 'debtor_days') return 'days';
    if (key === 'clients_billed') return 'number';
    return 'percent';
  }

  private effectiveDateRange(
    startDate: string | null,
    endDate: string | null,
    filter: CrossFilter | null
  ): { startDate: string | null; endDate: string | null } {
    return effectiveDateRange(startDate, endDate, filter);
  }

  private effectiveRevenueDrillState(
    drillState: DrillState,
    startDate: string | null,
    endDate: string | null
  ): DrillState {
    if (this.revenueManualOverride || !startDate || !endDate) {
      return drillState;
    }

    const start = new Date(startDate);
    const end = new Date(endDate);
    const days = Math.floor((end.getTime() - start.getTime()) / 86_400_000) + 1;
    if (days <= 31) {
      return { level: 'day', context: null };
    }
    if (days <= 90) {
      return { level: 'week', context: null };
    }
    if (days <= 365) {
      return { level: 'month', context: null };
    }
    return { level: 'year', context: null };
  }

  /** Mirrors effectiveRevenueDrillState's day/week/month thresholds for the Cash Flow Trend chart. */
  private effectiveCashFlowGranularity(
    manual: DashboardDrillLevel | string,
    startDate: string | null,
    endDate: string | null
  ): DashboardDrillLevel {
    if (this.cashFlowManualOverride || !startDate || !endDate) {
      return manual as DashboardDrillLevel;
    }
    const start = new Date(startDate);
    const end = new Date(endDate);
    const days = Math.floor((end.getTime() - start.getTime()) / 86_400_000) + 1;
    if (days <= 31) return 'day';
    if (days <= 90) return 'week';
    if (days <= 365) return 'month';
    return 'year';
  }

  private revenueRangeLabel(startDate: string | null, endDate: string | null): string {
    if (!startDate || !endDate) return 'FY 25/26';
    return this.formatExactDateRange(new Date(startDate), new Date(endDate));
  }

  private activeFilterLabel(startDate?: string | null, endDate?: string | null, contactId?: string | null): string {
    if (contactId && startDate && endDate) return `${startDate} to ${endDate} - selected contact`;
    if (contactId) return 'Selected contact';
    if (startDate && endDate) return `${startDate} to ${endDate}`;
    return 'Current filter';
  }
  private publishHeaderFilterOptions(res: ChartData, contactId?: string | null): void {
    const months = this.buildMonthFilterOptions(res);
    const options: { months: DashboardFilterOption[]; clients?: DashboardFilterOption[] } = { months };

    if (!contactId && res.revenueByClient?.length) {
      options.clients = res.revenueByClient
        .filter(client => (client.contactId || client.clientName) && Number(client.revenue) > 0)
        .map(client => ({
          label: client.clientName,
          value: client.contactId ?? client.clientName,
          contactId: client.contactId,
          contactName: client.clientName,
        }));
    }

    this.dashboardState.setHeaderFilterOptions(options);
  }

  private buildMonthFilterOptions(res: ChartData): DashboardFilterOption[] {
    const labels = new Map<string, string>();
    const addMonth = (key: string | null | undefined, label?: string | null) => {
      if (!key) return;
      const match = key.match(/^(\d{4}-\d{2})/);
      if (!match) return;
      const monthKey = match[1];
      labels.set(monthKey, label || this.formatMonthYear(monthKey));
    };

    (res.monthlyRevenue ?? []).forEach(row => addMonth(row.month, row.monthLabel));
    (res.financialOverview ?? []).forEach(row => addMonth(row.period, row.periodLabel));
    (res.revenueByPeriod ?? []).forEach(row => addMonth(row.period, row.periodLabel));

    return Array.from(labels.entries())
      .sort(([a], [b]) => b.localeCompare(a))
      .map(([value, label]) => ({ value, label }));
  }

  private loadChartData(
    drillLevel: DrillLevel,
    periodContext: string | null,
    startDate?: string | null,
    endDate?: string | null,
    status?: string[],
    contactId?: string | null,
    invoiceType?: string,
    cashFlowGranularity?: string,
    metric?: string
  ): void {
    const requestSeq = ++this.chartRequestSeq;
    this.dashboardService.getChartData(drillLevel, periodContext, startDate, endDate, status, contactId, invoiceType, cashFlowGranularity, metric).subscribe({
      next: (res) => {
        if (requestSeq !== this.chartRequestSeq) return;
        if (res) {
          this.lastChartData = res;
          this.revenueByPeriod = res.revenueByPeriod;
          this.revenueByClient = res.revenueByClient;
          this.publishHeaderFilterOptions(res, contactId);
          // tableRows is set inside setupChartData with cross-filter applied
          this.setupChartData(res);

          // Populate Activity & Alerts
          this.alerts = [
            { icon: 'pi-sync text-primary', title: 'Data Sync Success', subtitle: 'Imported latest records from Xero', timestamp: 'Just now' },
            { icon: 'pi-check-circle text-positive', title: 'Formulas Verified', subtitle: 'Calculated NFI, Margins and DSO', timestamp: '2 mins ago' }
          ];
        }
      },
      error: (err) => {
        if (requestSeq !== this.chartRequestSeq) return;
        console.error('Failed to load chart data:', err);
      }
    });
  }

  private setupChartData(res: ChartData): void {
    const tokens = getComputedStyle(document.documentElement);

    const accent = tokens.getPropertyValue('--color-accent').trim();
    const accentBg = tokens.getPropertyValue('--color-accent-bg').trim();
    const chartSecondary = tokens.getPropertyValue('--color-chart-secondary').trim();
    const filter = this.activeCrossFilter;

    // ── Client-side row filtering — applies BOTH contact and period to tables/charts ──
    // This is client-side refinement of already API-filtered data. The API already
    // honours startDate/endDate/contactId; this layer adds client-name matching when
    // the API only accepted contactId (which can differ if contact search was by name).
    const filterInvoice = (inv: InvoiceTableRow): boolean => {
      if (filter?.contactName && inv.clientName !== filter.contactName) return false;
      if (filter?.month && inv.invoiceDate && !inv.invoiceDate.startsWith(filter.month)) return false;
      return true;
    };
    const filteredInvoices = filter ? res.recentInvoices.filter(filterInvoice) : res.recentInvoices;

    this.tableRows  = filteredInvoices;

    // ── Chart 1: Financial Overview (Mixed) ──
    this.financialOverviewData = res.financialOverview;

    // ── Chart 2: Cash Flow Trend ──
    this.trendChartData = this.buildTrendChartData(res, filter, accent, accentBg, chartSecondary);

    // ── Chart 3: Revenue Breakdown ──
    const breakdown = this.buildBreakdownChartData(res, filter);
    this.breakdownChartData = breakdown.chartData;
    this.breakdownDetails = breakdown.details;
    this.breakdownCenterValue = this.formatValue(breakdown.totalRevenue, 'currency');
    this.breakdownCenterLabel = `${breakdown.totalInvoiceCount} Invoices`;
  }

  // ── Cash Flow Trend: granularity helpers ─────────────────────────────────

  /** Return a canonical bucket key for an ISO date string at the requested granularity. */
  private trendBucketKey(isoDate: string, granularity: string): string {
    const d = new Date(isoDate);
    if (isNaN(d.getTime())) return '';
    if (granularity === 'year') {
      return String(d.getFullYear());
    }
    if (granularity === 'quarter') {
      const q = Math.floor(d.getMonth() / 3) + 1;
      return `${d.getFullYear()}-Q${q}`;
    }
    if (granularity === 'month') {
      return isoDate.substring(0, 7); // yyyy-MM
    }
    if (granularity === 'week') {
      // ISO week: Monday as week start
      const tmp = new Date(d);
      tmp.setHours(0, 0, 0, 0);
      tmp.setDate(tmp.getDate() - ((tmp.getDay() + 6) % 7));
      return tmp.toISOString().substring(0, 10);
    }
    // day
    return isoDate.substring(0, 10);
  }

  /** Format a bucket key into a readable label for the chart x-axis. */
  private trendBucketLabel(key: string, granularity: string): string {
    if (granularity === 'year') return key.length >= 4 ? key.substring(0, 4) : key;
    if (granularity === 'quarter') return key; // e.g. "2025-Q2"
    if (granularity === 'month') {
      const [y, m] = key.split('-');
      return new Date(+y, +m - 1, 1).toLocaleDateString('en-GB', { month: 'short', year: '2-digit' });
    }
    if (granularity === 'week') {
      const d = new Date(key);
      return `w/c ${d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`;
    }
    // day
    const d = new Date(key);
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  }

  private buildTrendChartData(
    res: ChartData, filter: CrossFilter | null,
    accent: string, accentBg: string, chartSecondary: string,
  ): any {
    const granularity = this.activeTrendLevel;

    // Collect all invoices (billed) — apply contact filter if active
    const points = res.cashFlowTrend ?? [];
    const labels = points.map(x => this.trendBucketLabel(x.period, granularity));
    const billedData = points.map(x => x.billedRevenue);
    const collectedData = points.map(x => x.cashCollected);
    const seriesLabels = this.cashFlowSeriesLabels(this.currentInvoiceType);

    return {
      labels,
      datasets: [
        {
          label: seriesLabels.billed,
          data: billedData,
          borderColor: accent,
          backgroundColor: accentBg,
          fill: 'origin',
          tension: 0.15,
          borderWidth: 4,
          pointRadius: 0,
          pointHoverRadius: 0
        },
        {
          label: seriesLabels.cash,
          data: collectedData,
          borderColor: chartSecondary,
          backgroundColor: 'transparent',
          fill: 0,
          tension: 0.15,
          borderWidth: 4,
          pointRadius: 0,
          pointHoverRadius: 8,
          pointHoverBackgroundColor: accent,
          pointHoverBorderColor: chartSecondary,
          pointHoverBorderWidth: 4
        }
      ]
    };
  }

  private cashFlowSeriesLabels(invoiceType: string): { billed: string; cash: string } {
    if (invoiceType === 'sales') {
      return { billed: 'Billed Revenue', cash: 'Cash Collected' };
    }
    if (invoiceType === 'purchases') {
      return { billed: 'Purchase Bills', cash: 'Cash Paid' };
    }
    return { billed: 'Billed Revenue', cash: 'Cash Movement' };
  }

  private buildBreakdownChartData(
    res: ChartData, filter: CrossFilter | null,
  ): { chartData: any; details: RadialChartDetail[]; totalRevenue: number; totalInvoiceCount: number } {
    // Use rank-ordered palette from the shared chart theme
    const palette = breakdownPalette().slice(0, 10);

    // Build the colour map from the full unfiltered client list so it persists
    // across contact drill-downs. Only update when we have the complete list.
    if (!filter?.contactId && res.revenueByClient?.length) {
      this.breakdownColorMap.clear();
      res.revenueByClient.forEach((c, idx) => {
        if (c.contactId) this.breakdownColorMap.set(c.contactId, idx);
      });
    }

    let revenues: number[];
    let chartData: any;
    let totalInvoiceCount: number;
    let contactIds: (string | null)[];

    revenues = res.revenueByClient.map(x => x.revenue);
    totalInvoiceCount = res.revenueByClient.reduce((sum, x) => sum + x.invoiceCount, 0);
    contactIds = res.revenueByClient.map(x => x.contactId);
    chartData = {
      labels: res.revenueByClient.map(x => x.clientName),
      datasets: [{ data: revenues, backgroundColor: palette, borderColor: getComputedStyle(document.documentElement).getPropertyValue('--color-card-bg').trim(), borderWidth: 3, hoverOffset: 8, spacing: 2 }]
    };

    // Persist contactIds so breakdownViewModel can use real IDs for filter logic
    this.breakdownContactIds = contactIds;

    const totalRevenue = revenues.reduce((sum, v) => sum + v, 0);
    const details: RadialChartDetail[] = revenues.map(revenue => ({
      amount: this.formatValue(revenue, 'currency'),
      percentage: totalRevenue > 0 ? `${((revenue / totalRevenue) * 100).toFixed(1)}%` : '0%',
    }));

    return { chartData, details, totalRevenue, totalInvoiceCount };
  }

  private periodKeyForDate(dateStr: string, level: DrillLevel): string {
    if (level === 'month') return dateStr.substring(0, 7);
    if (level === 'day') return dateStr.substring(0, 10);
    const d = new Date(dateStr);
    const dayOfWeek = d.getDay(); // 0=Sun..6=Sat
    const diffToMonday = (dayOfWeek + 6) % 7;
    const monday = new Date(d);
    monday.setDate(d.getDate() - diffToMonday);
    return monday.toISOString().substring(0, 10);
  }

  private formatValue(value: number | null, format: string, fractionDigits?: number): string {
    if (value === null || value === undefined) return 'N/A';
    switch (format) {
      case 'currency':
        return new Intl.NumberFormat('en-GB', {
          style: 'currency',
          currency: 'GBP',
          maximumFractionDigits: 0
        }).format(value);
      case 'percent':
        return new Intl.NumberFormat('en-GB', {
          minimumFractionDigits: fractionDigits ?? 1,
          maximumFractionDigits: fractionDigits ?? 1
        }).format(value) + '%';
      case 'days':
        return `${Math.round(value)}`;
      default:
        return value.toString();
    }
  }

  private getChartOptions() {
    const tokens = getComputedStyle(document.documentElement);
    const chartSurface = tokens.getPropertyValue('--color-chart-surface').trim();
    const cardBorder = tokens.getPropertyValue('--color-chart-grid').trim()
      || tokens.getPropertyValue('--color-card-border').trim();
    const textPrimary = tokens.getPropertyValue('--color-text-primary').trim();
    const textSecondary = tokens.getPropertyValue('--color-text-secondary').trim();
    const muted = tokens.getPropertyValue('--color-text-muted').trim();
    const accent = tokens.getPropertyValue('--color-accent').trim();
    const cardBg = tokens.getPropertyValue('--color-card-bg').trim();
    return {
      trend: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        hover: { mode: 'index', intersect: false },
        elements: {
          point: {
            radius: 0,
            hoverRadius: 6,
            hoverBorderWidth: 2.5,
            hoverBorderColor: cardBg,
            hoverBackgroundColor: (context: any) => {
              return context.dataset.borderColor;
            }
          }
        },
        plugins: {
          legend: {
            display: false
          },
          tooltip: {
            mode: 'index',
            intersect: false,
            backgroundColor: chartSurface,
            borderColor: cardBorder,
            borderWidth: 1,
            padding: 10,
            cornerRadius: 8,
            titleColor: textPrimary,
            titleFont: { size: 13, weight: 'semibold' },
            bodyColor: textPrimary,
            bodyFont: { size: 12 },
            footerColor: accent,
            footerFont: { size: 12, weight: 'medium' },
            footerBorderWidth: 1,
            footerBorderColor: cardBorder,
            footerMarginTop: 8,
            footerPadding: 6,
            displayColors: true,
            boxWidth: 6,
            boxHeight: 6,
            boxPadding: 4,
            usePointStyle: true,
            axisPointer: {
              type: 'line',
              lineStyle: {
                color: muted,
                width: 1.5,
                type: 'dashed'
              }
            },
            callbacks: {
              title: (tooltipItems: any) => {
                return tooltipItems[0].label;
              },
              label: (context: any) => {
                const val = context.raw;
                const valFormatted = new Intl.NumberFormat('en-GB', {
                  style: 'currency',
                  currency: 'GBP',
                  maximumFractionDigits: 2
                }).format(val);
                const label = context.datasetIndex === 0 ? 'Billed:' : 'Collected:';
                const spaces = context.datasetIndex === 0 ? '      ' : '   ';
                return ` ${label}${spaces}${valFormatted}`;
              },
              footer: (tooltipItems: any) => {
                return 'View details ↗';
              }
            }
          }
        },
        scales: {
          x: {
            grid: { display: false },
            border: { display: false },
            ticks: {
              color: textSecondary,
              font: { size: 9 },
              maxRotation: 30,
              minRotation: 0,
              autoSkip: true
            }
          },
          y: {
            min: 0,
            max: 30000,
            ticks: {
              stepSize: 10000,
              color: textSecondary,
              font: { size: 10 },
              padding: 8,
              callback: (value: any) => {
                if (value === 0) return '0';
                if (value >= 1000) return `£${(value / 1000).toFixed(0)}k`;
                return `£${value}`;
              }
            },
            grid: { color: cardBorder, drawTicks: false, borderDash: [4, 4] },
            border: { display: false }
          }
        }
      }
    };
  }

  onTrendLevelClick(lvl: string): void {
    // Explicit user pick overrides date-range auto-selection until Reset All.
    this.cashFlowManualOverride = true;
    // Emit to the subject — combineLatest will fire, syncing activeTrendLevel
    // and calling setupChartData with the new granularity.
    this.cashFlowGranularity$.next(lvl);
  }

  onTrendDrillUp(): void {
    const next = this.stepCashFlowLevel(-1);
    if (!next) return;
    this.cashFlowManualOverride = true;
    this.cashFlowGranularity$.next(next);
  }

  onTrendDrillDown(): void {
    const next = this.stepCashFlowLevel(1);
    if (!next) return;
    this.cashFlowManualOverride = true;
    this.cashFlowGranularity$.next(next);
  }

  private stepCashFlowLevel(direction: -1 | 1): string | null {
    const order = ['year', 'quarter', 'month', 'week', 'day'];
    const index = order.indexOf(this.activeTrendLevel);
    const next = order[index + direction];
    return next ?? null;
  }

  private resetCashFlowGranularity(): void {
    this.cashFlowManualOverride = false;
    if (this.cashFlowGranularity$.value !== 'year') {
      this.cashFlowGranularity$.next('year');
    }
  }

  onTrendPointSelect(evt: any): void {
    if (!evt || !evt.element) return;
    const index = evt.element.index;
    const point = this.lastChartData?.cashFlowTrend?.[index];
    if (!point) return;

    const level = this.activeTrendLevel as DrillLevel;
    const range = this.rangeForPeriodClick(point.period, level);
    if (this.isSamePeriodFilter(range)) {
      this.dashboardState.clearPeriodFilter();
    } else {
      this.applyPeriodCrossFilter(range);
    }
  }

  onClientRowClick(clientName: string): void {
    const current = this.activeCrossFilter?.contactName;
    if (current === clientName) {
      // Toggle off: clear just the contact dimension, preserve period
      this.dashboardState.clearContactFilter();
    } else {
      const matching = this.revenueByClient.find(c => c.clientName === clientName);
      const contactId = matching?.contactId || null;
      // Merge contact into existing period filter (intersection)
      this.dashboardState.mergeCrossFilter({
        contactName: clientName,
        contactId: contactId ?? undefined
      });
    }
  }

  /** Cross-highlight only — does NOT change global filter state */
  onBreakdownHover(index: number | null): void {
    this.hoveredBreakdownIndex = index;
  }

  /**
   * Normal selection using real contactId — applies shared global client filter.
   * Use contactId for logic, name only for display.
   */
  onBreakdownClientSelectById(payload: { contactId: string | null; contactName: string }): void {
    const current = this.activeCrossFilter?.contactId;
    if (current === payload.contactId && payload.contactId != null) {
      // Toggle off — preserve period filter
      this.dashboardState.clearContactFilter();
    } else {
      this.dashboardState.mergeCrossFilter({
        contactId: payload.contactId ?? undefined,
        contactName: payload.contactName,
      });
    }
  }

  /**
   * Drill Through — applies the client filter to shared state and
   * navigates to the invoices route pre-filtered to that client.
   */
  onBreakdownDrillThrough(payload: { contactId: string | null; contactName: string }): void {
    if (payload.contactId) {
      this.dashboardState.mergeCrossFilter({
        contactId: payload.contactId,
        contactName: payload.contactName,
      });
    }
    this.navigateToRecords(null, payload.contactId);
  }

  onRevenueDrillUp(): void {
    const current = this.revenueDrillState.level;
    const previous = this.previousRevenueLevel(current);
    if (!previous) return;

    this.revenueManualOverride = true;
    const parentPeriod = this.parentPeriodForCurrentRevenueState(current, this.revenueDrillState.context);

    if (previous === 'year') {
      this.dashboardState.resetDrillState(this.revenueChartKey);
    } else {
      this.dashboardState.setDrillState(this.revenueChartKey, previous, parentPeriod);
    }

    if (parentPeriod) {
      this.applyPeriodCrossFilter(this.rangeForPeriodClick(parentPeriod, previous));
    } else if (previous === 'year') {
      this.dashboardState.clearPeriodFilter();
    }
  }

  onRevenueDrillDown(): void {
    const current = this.revenueDrillState.level;
    const next = this.nextRevenueLevel(current);
    if (!next) return;

    this.revenueManualOverride = true;
    this.dashboardState.setDrillState(this.revenueChartKey, next, null);
  }

  private parentPeriodForCurrentRevenueState(level: DrillLevel, context: string | null): string | null {
    if (!context) return null;
    if (level === 'day') return context.substring(0, 10);
    if (level === 'week') return context.substring(0, 7);
    if (level === 'month') return context;
    if (level === 'quarter') return context.substring(0, 4);
    return null;
  }

  onRevenueNextLevel(): void {
    this.onRevenueDrillDown();
  }

  onRevenueExpandAll(): void {
    this.onRevenueDrillDown();
  }

  onRevenueOpenDetails(): void {
    this.navigateToRecords();
  }

  onDashboardContextAction(payload: DashboardContextPayload): void {
    switch (payload.action) {
      case 'drillDown':
        this.handleContextDrillDown(payload);
        break;
      case 'drillUp':
        this.handleContextDrillUp(payload);
        break;
      case 'keepOnly':
        this.handleContextKeepOnly(payload);
        break;
      case 'clearFilter':
        this.handleContextClear(payload);
        break;
      case 'viewRecords':
        this.handleContextViewRecords(payload);
        break;
      case 'copyValue':
        this.copyContextValue(payload);
        break;
    }
  }

  private handleContextDrillDown(payload: DashboardContextPayload): void {
    if (payload.source === 'revenue') {
      if (typeof payload.index === 'number') {
        this.drillRevenueIntoIndex(payload.index);
      } else {
        this.onRevenueDrillDown();
      }
      return;
    }
    if (payload.source === 'cashFlow') {
      this.onTrendDrillDown();
      return;
    }
    if (payload.source === 'breakdown' && typeof payload.index === 'number') {
      this.onBreakdownSegmentClick(payload.index);
    }
  }

  private handleContextDrillUp(payload: DashboardContextPayload): void {
    if (payload.source === 'revenue') {
      this.onRevenueDrillUp();
      return;
    }
    if (payload.source === 'cashFlow') {
      this.onTrendDrillUp();
      return;
    }
    if (payload.source === 'breakdown' || payload.source === 'invoice') {
      this.dashboardState.clearContactFilter();
    }
  }

  private handleContextKeepOnly(payload: DashboardContextPayload): void {
    if (payload.source === 'revenue' && typeof payload.index === 'number') {
      this.keepOnlyRevenue(payload.index);
      return;
    }
    if (payload.source === 'cashFlow' && typeof payload.index === 'number') {
      this.keepOnlyCashFlow(payload.index);
      return;
    }
    if (payload.source === 'breakdown' && typeof payload.index === 'number') {
      this.keepOnlyBreakdown(payload.index);
      return;
    }
    if (payload.source === 'invoice' && payload.row) {
      this.keepOnlyInvoice(payload.row);
    }
  }

  private handleContextClear(payload: DashboardContextPayload): void {
    if (payload.source === 'revenue' || payload.source === 'cashFlow') {
      this.dashboardState.clearPeriodFilter();
      return;
    }
    this.dashboardState.clearContactFilter();
  }

  private handleContextViewRecords(payload: DashboardContextPayload): void {
    if (payload.source === 'invoice' && payload.row) {
      this.navigateToRecords(payload.row);
      return;
    }
    if (payload.source === 'breakdown' && typeof payload.index === 'number') {
      const item = this.revenueByClient[payload.index];
      this.navigateToRecords(null, item?.contactId ?? null);
      return;
    }
    this.navigateToRecords();
  }

  private drillRevenueIntoIndex(index: number): void {
    const item = this.revenueByPeriod[index];
    const nextLevel = this.nextRevenueLevel(this.revenueDrillState.level);
    if (!item || !nextLevel) return;
    this.revenueManualOverride = true;
    this.dashboardState.setDrillState(this.revenueChartKey, nextLevel, item.period);
  }

  private keepOnlyRevenue(index: number): void {
    const item = this.revenueByPeriod[index];
    if (!item) return;
    const range = this.rangeForPeriodClick(item.period, this.revenueDrillState.level);
    if (this.isSamePeriodFilter(range)) {
      this.dashboardState.clearPeriodFilter();
    } else {
      this.applyPeriodCrossFilter(range);
    }
  }

  private keepOnlyCashFlow(index: number): void {
    const point = this.lastChartData?.cashFlowTrend?.[index];
    if (!point) return;
    const range = this.rangeForPeriodClick(point.period, this.activeTrendLevel as DrillLevel);
    if (this.isSamePeriodFilter(range)) {
      this.dashboardState.clearPeriodFilter();
    } else {
      this.applyPeriodCrossFilter(range);
    }
  }

  private keepOnlyBreakdown(index: number): void {
    const item = this.revenueByClient[index];
    if (!item) return;
    if ((item.contactId && this.activeCrossFilter?.contactId === item.contactId) ||
        (!item.contactId && this.activeCrossFilter?.contactName === item.clientName)) {
      this.dashboardState.clearContactFilter();
      return;
    }
    this.dashboardState.mergeCrossFilter({
      contactId: item.contactId ?? undefined,
      contactName: item.clientName,
    });
  }

  private keepOnlyInvoice(row: any): void {
    const contactName = row?.clientName;
    const contactId = row?.contactId ?? null;
    if (!contactName) return;
    if ((contactId && this.activeCrossFilter?.contactId === contactId) ||
        (!contactId && this.activeCrossFilter?.contactName === contactName)) {
      this.dashboardState.clearContactFilter();
      return;
    }
    this.dashboardState.mergeCrossFilter({
      contactId: contactId ?? undefined,
      contactName,
    });
  }

  private navigateToRecords(row?: any, contactIdOverride?: string | null): void {
    const rowType = String(row?.type ?? '').toUpperCase();
    const route = this.currentInvoiceType === 'purchases' || rowType === 'ACCPAY'
      ? '/purchases'
      : '/sales';
    const queryParams = this.recordQueryParams(row, contactIdOverride);
    this.router.navigate([route], { queryParams });
  }

  private navigateToRecordsForRange(startDate: string | null, endDate: string | null): void {
    const route = this.currentInvoiceType === 'purchases' ? '/purchases' : '/sales';
    const queryParams = this.recordQueryParams();
    if (startDate) queryParams['startDate'] = startDate;
    if (endDate) queryParams['endDate'] = endDate;
    this.router.navigate([route], { queryParams });
  }

  private recordQueryParams(row?: any, contactIdOverride?: string | null): Record<string, string> {
    const query: Record<string, string> = {};
    const contactId = contactIdOverride ?? row?.contactId ?? this.activeCrossFilter?.contactId ?? null;
    if (contactId) query['contactId'] = contactId;
    if (this.currentStartDate) query['startDate'] = this.currentStartDate;
    if (this.currentEndDate) query['endDate'] = this.currentEndDate;
    if (this.currentStatus.length > 0) query['status'] = this.currentStatus.join(',');
    if (this.currentInvoiceType !== 'all') query['invoiceType'] = this.currentInvoiceType;
    return query;
  }

  private copyContextValue(payload: DashboardContextPayload): void {
    const value = this.contextValueText(payload);
    if (!value) return;
    navigator.clipboard?.writeText(value).catch(() => undefined);
  }

  private contextValueText(payload: DashboardContextPayload): string {
    if (payload.source === 'revenue' && typeof payload.index === 'number') {
      const item = this.revenueByPeriod[payload.index];
      return item ? `${item.periodLabel}: ${this.formatValue(item.revenue, 'currency')}` : '';
    }
    if (payload.source === 'cashFlow' && typeof payload.index === 'number') {
      const point = this.lastChartData?.cashFlowTrend?.[payload.index];
      if (!point) return '';
      return `${point.period}: billed ${this.formatValue(point.billedRevenue, 'currency')}, cash ${this.formatValue(point.cashCollected, 'currency')}`;
    }
    if (payload.source === 'breakdown' && typeof payload.index === 'number') {
      const item = this.revenueByClient[payload.index];
      return item ? `${item.clientName}: ${this.formatValue(item.revenue, 'currency')}` : '';
    }
    if (payload.source === 'invoice' && payload.row) {
      const row = payload.row as any;
      return `${row.invoiceNumber ?? ''} ${row.clientName ?? ''} ${this.formatValue(row.total ?? 0, 'currency')}`.trim();
    }
    return '';
  }
}
